import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

/**
 * The "you are still recording" alarm.
 *
 * A consultation is recorded from a modeless dialog that is deliberately easy to ignore — it
 * has no backdrop, it can be minimised to a 300px bar, and the clinician spends the whole
 * recording looking at the patient file behind it. The failure this guards against is a
 * recording left running into the next consultation, so the cue has to live outside the
 * dialog: a border around the whole viewport, on top of every overlay, that cannot be
 * mistaken for part of any one screen.
 *
 * `activate`/`deactivate` are reference counted because more than one AI dialog can be open
 * at once (a ward round and a patient note); the border belongs to "something is recording",
 * not to a particular dialog, so it only lifts when the last one stops.
 */
@Injectable({
  providedIn: 'root'
})
export class RecordingAlertService {
  /** Matches the selector in `_recording-alert.scss`. */
  private static readonly BODY_CLASS = 'ai-recording-active';

  private activeCount = 0;
  private readonly isBrowser: boolean;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /** Show the screen border. Safe to call repeatedly for the same recording. */
  activate(): void {
    this.activeCount++;
    this.sync();
  }

  /**
   * Drop one recording's claim on the border. Callers pair this with `activate` from
   * `stopRecording`, `pauseRecording` and `ngOnDestroy`, so the count is clamped at zero
   * rather than trusting every path to be perfectly balanced.
   */
  deactivate(): void {
    this.activeCount = Math.max(0, this.activeCount - 1);
    this.sync();
  }

  /** Belt and braces for teardown paths that cannot know how many claims they hold. */
  reset(): void {
    this.activeCount = 0;
    this.sync();
  }

  /**
   * A short chime on start, because the visual cue is useless to someone who has already
   * looked away. Playback is best-effort: it is triggered by the Record tap, so autoplay
   * policy allows it, but a device with audio routed elsewhere (or a browser that refuses)
   * must not take the recording down with it.
   */
  playStartChime(): void {
    if (!this.isBrowser) return;

    try {
      const audio = new Audio('/assets/sounds/recording-start.wav');
      audio.volume = 0.6;
      void audio.play().catch(err => console.warn('Recording start chime blocked:', err));
    } catch (err) {
      console.warn('Recording start chime failed:', err);
    }
  }

  private sync(): void {
    if (!this.isBrowser) return;

    this.document.body.classList.toggle(RecordingAlertService.BODY_CLASS, this.activeCount > 0);
  }
}
